import { SettingsManager } from "@viamap/viamap2-common";
import { PromiseCanvas, PromiseImage } from "src/functions/PaperCanvas";

export const COMPANY_LOGO_SETTING_KEY = "company.logo";
export const COMPANY_LOGO_DARK_SETTING_KEY = "company.logoForDarkBackground";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_LOGO_EDGE = 800;

function isHttpUrl(value: string): boolean {
   return /^https?:\/\//i.test(value);
}

function isImageSource(value: unknown): value is string {
   return typeof value === "string" && (value.startsWith("data:image/") || isHttpUrl(value));
}

/** License and customer settings still supply the plot logo as `plot.image.url`. `"default"` means there is no custom file. */
function legacyPlotLogoUrl(): string {
   const plotUrl = SettingsManager.getSystemSetting("plot.image.url", "default", true);
   if (typeof plotUrl !== "string") {
      return "";
   }
   const trimmed = plotUrl.trim();
   if (!trimmed || trimmed === "default") {
      return "";
   }
   return trimmed;
}

/** Organization logo file, or the older plot logo URL until a file has been uploaded. A dark-background file is used only when one has been uploaded. */
export function companyLogoSource(options?: { darkBackground?: boolean }): string {
   if (options?.darkBackground) {
      const dark = SettingsManager.getSystemSetting(COMPANY_LOGO_DARK_SETTING_KEY, "", true);
      if (isImageSource(dark)) {
         return dark;
      }
   }
   const file = SettingsManager.getSystemSetting(COMPANY_LOGO_SETTING_KEY, "", true);
   if (isImageSource(file)) {
      return file;
   }
   return legacyPlotLogoUrl();
}

/** Servituterklæringen uses the organization logo, and otherwise the existing system image. */
export function servitutDeclarationLogoSource(): string {
   const company = companyLogoSource();
   if (company) {
      return company;
   }
   if (SettingsManager.getSystemSetting("datarapportImageDisabled", false)) {
      return "";
   }
   return SettingsManager.getSystemSetting("datarapportImageUrl", "") || "";
}

export async function loadCompanyLogoDataUrl(): Promise<string | null> {
   const source = companyLogoSource();
   if (!source) {
      return null;
   }
   if (source.startsWith("data:image/")) {
      return source;
   }
   const canvas = await PromiseCanvas(source);
   if (!canvas) {
      return null;
   }
   try {
      return canvas.toDataURL("image/png");
   } catch {
      return null;
   }
}

function isSvgFile(file: File): boolean {
   return file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
}

function pixelLength(value: string | null): number {
   const match = /^\s*(\d+(?:\.\d+)?)(px)?\s*$/.exec(value || "");
   const length = match ? Number(match[1]) : 0;
   return length > 0 ? length : 0;
}

/** Give the SVG a pixel size so the browser can draw it. Percent sizes fall back to the viewBox. */
function svgWithPixelSize(source: string): { markup: string; width: number; height: number } | null {
   const doc = new DOMParser().parseFromString(source, "image/svg+xml");
   if (doc.querySelector("parsererror")) {
      return null;
   }
   const svg = doc.documentElement;
   if (svg.tagName.toLowerCase() !== "svg") {
      return null;
   }
   let width = pixelLength(svg.getAttribute("width"));
   let height = pixelLength(svg.getAttribute("height"));
   if (!(width > 0) || !(height > 0)) {
      const box = (svg.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
      if (box.length === 4 && box[2] > 0 && box[3] > 0) {
         width = box[2];
         height = box[3];
      }
   }
   if (!(width > 0) || !(height > 0)) {
      return null;
   }
   svg.setAttribute("width", String(width));
   svg.setAttribute("height", String(height));
   return { markup: new XMLSerializer().serializeToString(svg), width, height };
}

export async function imageFileToPngDataUrl(file: File): Promise<string> {
   const svg = isSvgFile(file);
   if (!svg && !file.type.startsWith("image/")) {
      throw new Error("unsupported");
   }
   if (file.size > MAX_LOGO_BYTES) {
      throw new Error("too-large");
   }
   let source = "";
   let knownWidth = 0;
   let knownHeight = 0;
   if (svg) {
      const sized = svgWithPixelSize(await file.text());
      if (!sized) {
         throw new Error("unsupported");
      }
      source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized.markup)}`;
      knownWidth = sized.width;
      knownHeight = sized.height;
   } else {
      source = await new Promise<string>((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => {
            if (typeof reader.result === "string") {
               resolve(reader.result);
               return;
            }
            reject(new Error("unsupported"));
         };
         reader.onerror = () => reject(reader.error || new Error("unsupported"));
         reader.readAsDataURL(file);
      });
   }
   const image = await PromiseImage(source);
   if (!image) {
      throw new Error("unsupported");
   }
   const pixelWidth = image.naturalWidth || knownWidth;
   const pixelHeight = image.naturalHeight || knownHeight;
   if (!(pixelWidth > 0) || !(pixelHeight > 0)) {
      throw new Error("unsupported");
   }
   const scale = Math.min(1, MAX_LOGO_EDGE / Math.max(pixelWidth, pixelHeight));
   const width = Math.max(1, Math.round(pixelWidth * scale));
   const height = Math.max(1, Math.round(pixelHeight * scale));
   const canvas = document.createElement("canvas");
   canvas.width = width;
   canvas.height = height;
   const context = canvas.getContext("2d");
   if (!context) {
      throw new Error("unsupported");
   }
   context.drawImage(image, 0, 0, width, height);
   return canvas.toDataURL("image/png");
}
